import { Helmet } from "react-helmet-async";
import { lazy, Suspense } from "react";
import { TopBanner } from "@/components/TopBanner";
import { Header } from "@/components/Header";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { HeroSection } from "@/components/HeroSection";
import { Footer } from "@/components/Footer";

// Lazy load components below the fold for better initial load performance
const CategoriasSection = lazy(() => import("@/components/CategoriasSection").then(m => ({ default: m.CategoriasSection })));
const IncludedSection = lazy(() => import("@/components/IncludedSection").then(m => ({ default: m.IncludedSection })));
const HowItWorksSection = lazy(() => import("@/components/HowItWorksSection").then(m => ({ default: m.HowItWorksSection })));
const BenefitsSection = lazy(() => import("@/components/BenefitsSection").then(m => ({ default: m.BenefitsSection })));
const TrustSection = lazy(() => import("@/components/TrustSection").then(m => ({ default: m.TrustSection })));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection").then(m => ({ default: m.TestimonialsSection })));
const ComparisonSection = lazy(() => import("@/components/ComparisonSection").then(m => ({ default: m.ComparisonSection })));
const FAQSection = lazy(() => import("@/components/FAQSection").then(m => ({ default: m.FAQSection })));
const FinalCTASection = lazy(() => import("@/components/FinalCTASection").then(m => ({ default: m.FinalCTASection })));

// Minimal loading placeholder
const SectionLoader = () => (
  <div className="py-16 lg:py-24 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-aceena-green border-t-transparent rounded-full animate-spin" />
  </div>
);

const structuredData = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Aceena",
  "description": "Consultoria técnica especializada em carro por assinatura. Mais de 30 anos de experiência no setor automotivo.",
  "url": "https://www.aceena.com.br",
  "telephone": "+55-11-98900-4165",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "BR"
  },
  "priceRange": "Gratuito",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": "50"
  },
  "sameAs": [
    "https://instagram.com/aceena.br",
    "https://linkedin.com/company/aceena",
    "https://facebook.com/aceena.br"
  ]
};

const Index = () => {
  return (
    <>
      <Helmet>
        <title>
          Carro por Assinatura com Curadoria Especializada | Aceena — Saiba
          Antes de Pedir Propostas
        </title>
        <meta
          name="description"
          content="Evite erros ao assinar um carro novo. Com 30+ anos de experiência, a Aceena orienta você em coberturas, serviços e contratos de assinatura com consultoria técnica imparcial. Fale com um especialista antes das locadoras."
        />
        <meta
          name="keywords"
          content="carro por assinatura consultoria, ajuda para assinar carro, entender contrato de assinatura, curadoria contratação automóvel, comparação de coberturas de contrato"
        />
        <link rel="canonical" href="https://www.aceena.com.br" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Carro por Assinatura com Curadoria Especializada | Aceena"
        />
        <meta
          property="og:description"
          content="Evite erros ao assinar um carro novo. Consultoria técnica imparcial com 30+ anos de experiência no setor automotivo."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.aceena.com.br" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Aceena" />
        <meta property="og:image" content="https://www.aceena.com.br/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Aceena - Consultoria em Carro por Assinatura" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Carro por Assinatura com Curadoria Especializada | Aceena"
        />
        <meta
          name="twitter:description"
          content="Evite erros ao assinar um carro novo. Consultoria técnica imparcial com 30+ anos de experiência."
        />
        <meta name="twitter:image" content="https://www.aceena.com.br/og-image.jpg" />

        {/* Additional SEO */}
        <meta name="author" content="Aceena" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="theme-color" content="#2D395B" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <TopBanner />
        <Header />
        <main>
          <HeroSection />
          <Suspense fallback={<SectionLoader />}>
            <CategoriasSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <IncludedSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <HowItWorksSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <BenefitsSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <TrustSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <TestimonialsSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <ComparisonSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <FAQSection />
          </Suspense>
          <Suspense fallback={<SectionLoader />}>
            <FinalCTASection />
          </Suspense>
        </main>
        <Footer />
        <WhatsAppButton />
        <CookieConsentBanner />
      </div>
    </>
  );
};

export default Index;
